/**
 * pages/ValidationConflits/ValidationConflits.js
 * Page dédiée à la résolution des conflits de validation
 */

import "./ValidationConflits.css";
import { ValidationPanel } from "../../components/ValidationPanel/ValidationPanel.js";

import logger from "../../utils/logger.js";

export class ValidationConflits {
  constructor(state) {
    this.state = state;
    this.element = null;
    this.validationPanel = null;

    if (window.location.hostname === "localhost") window.debug = this;

    // Écouter les changements d'état
    this.state.on("sourceUpdated", (data) => {
      if (data.source !== "conflictResolutions") {
        this.refresh();
      }
    });
  }

  hasAllData() {
    return this.state.canRunAnalysis();
  }

  refresh() {
    if (this.element) {
      const container = this.element.parentNode;
      this.unmount();
      this.mount(container);
    }
  }

  render() {
    const hasData = this.hasAllData();

    return `
      <div class="validation-conflits-page">
        <div class="page-header">
          <h1 class="page-title">🔍 Résolution des Conflits</h1>
          <p class="page-description">
            ${
              hasData
                ? "Résolvez les incohérences détectées dans vos données."
                : "Certaines données sont manquantes pour détecter les conflits."
            }
          </p>
        </div>

        <div class="page-content">
          ${!hasData ? this.renderIncompleteState() : this.renderReadyState()}
        </div>
      </div>
    `;
  }

  renderIncompleteState() {
    return `
      <div class="content-card welcome-state">
        <div class="card-icon">🔍</div>
        <h2 class="card-title">Prêt à détecter les conflits</h2>
        <p class="card-description">
          Importez vos données pour commencer l'analyse et détecter les incohérences 
          dans votre configuration de fibres optiques.
        </p>
        
        <div class="card-actions">
          <button class="btn btn-primary" onclick="window.app.state.setCurrentPage('config-global')">
            📁 Commencer l'import
          </button>
        </div>
      </div>
    `;
  }
  /* TODO check if really used and why ?*/
  renderReadyState() {
    return `
      <div class="validation-workspace">
        <div id="validation-panel-container">
          <!-- ValidationPanel sera monté ici -->
        </div>
      </div>
    `;
  }

  renderNoConflictsState() {
    return `
      <div class="content-card no-conflicts-state">
        <div class="card-icon">ℹ️</div>
        <h2 class="card-title">Aucun conflit détecté</h2>
        <p class="card-description">
          Vos données semblent cohérentes ou aucun conflit n'a pu être analysé.
          Assurez-vous d'avoir importé un dossier de données valide et complet.
        </p>
        <div class="info-suggestion">
          <p><strong>Pour détecter les conflits, vérifiez que vous avez importé :</strong></p>
          <ul>
            <li>Configuration des tiroirs (fichier JSON)</li>
            <li>Base ROP (fichier Excel)</li>
            <li>Rapports de fibres avec mesures SOR</li>
          </ul>
        </div>
        <div class="card-actions">
          <button class="btn btn-primary" onclick="window.app.state.setCurrentPage('config-global')">
            📁 Importer des données
          </button>
        </div>
      </div>
    `;
  }

  renderMissingSources() {
    const sources = this.state.getAllSources();
    const missing = Object.entries(sources)
      .filter(([_key, source]) => source.status !== "success")
      .map(([key, source]) => {
        const names = {
          configTiroirs: "Configuration Tiroirs",
          baseROP: "Base ROP",
          rapportFiberPvReussite: "Rapport Fiber PV Réussite",
          pvReussite: "PV Réussite",
          rapportFiberPvEchec: "Rapport Fiber PV Échec",
          pvEchec: "PV Échec",
          pvHs: "PV Hors Service",
        };
        const statusText = {
          empty: "Non chargé",
          loading: "En cours...",
          error: "Erreur",
        };
        const statusClass = {
          empty: "text-muted",
          loading: "status-warning",
          error: "status-error",
        };

        return `
          <div class="status-item">
            <span class="status-label">${names[key]}:</span>
            <span class="status-value ${statusClass[source.status]}">${statusText[source.status] || source.status}</span>
          </div>
        `;
      });

    return missing.length > 0
      ? missing.join("")
      : '<div class="status-complete">✓ Toutes les sources sont chargées</div>';
  }

  async mountValidationPanel() {
    const container = this.element.querySelector("#validation-panel-container");
    if (!container) return;

    try {
      this.validationPanel = new ValidationPanel(this.state, {});

      await this.validationPanel.mount(container);

      // CORRECTION : Détecter aucun conflit via le DOM après montage
      setTimeout(() => {
        const noConflictsElement = container.querySelector(".no-conflicts");
        if (noConflictsElement) {
          this.showNoConflictsMessage();
        }
      }, 100); // Petit délai pour laisser le ValidationPanel finir son rendu
    } catch (error) {
      logger.error(
        {
          context: "validation-conflits-page-mounting",
          error: error.message,
          stack: error.stack,
          operation: "mount-validation-panel",
          hasContainer: !!container,
          hasAllData: this.hasAllData(),
          hasState: !!this.state,
          containerSelector: "#validation-panel-container",
        },
        "Erreur montage ValidationPanel"
      );
      this.state.addToasterMessage("error", "Erreur lors du lancement de la validation");
    }
  }

  showNoConflictsMessage() {
    const workspace = this.element.querySelector(".validation-workspace");
    if (workspace) {
      workspace.innerHTML = this.renderNoConflictsState();
    }
  }

  bindEvents() {
    // Pas d'événements spécifiques - page autonome
  }

  async mount(container) {
    this.element = document.createElement("div");
    this.element.innerHTML = this.render();
    const mountedElement = this.element.firstElementChild;
    container.appendChild(mountedElement);
    this.element = mountedElement;

    this.bindEvents();

    // Monter le ValidationPanel si les données sont disponibles
    if (this.hasAllData()) {
      await this.mountValidationPanel();
    }

    return this.element;
  }

  unmount() {
    if (this.validationPanel) {
      this.validationPanel.unmount();
      this.validationPanel = null;
    }

    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
